import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
  isAnyOf,
} from "@reduxjs/toolkit";
import {
  addIndexationLogosToPublicationTypes,
  addIndexationLogoToPublicationTypes,
  dimensionsTypes,
  indexation_logoTypes,
  renameArticleTypesTypes,
} from "./dimensionTypes";
import dimensionsService from "./dimensionsService";
import { affiliationTypes } from "../affiliation/affiliationTypes";

interface dimensionState {
  articleTypes: dimensionsTypes[] | null;
  allArticleTypes: dimensionsTypes[] | null;
  articleStatus: dimensionsTypes[] | null;
  reviewStatus: dimensionsTypes[] | null;
  authorContributions: dimensionsTypes[] | null;
  publicationReviewType: dimensionsTypes[] | null;
  contributionList: dimensionsTypes[] | null;
  affiliations: affiliationTypes[] | null;
  indexation_logo: indexation_logoTypes[] | null;
  indexationLogosByPublication: indexation_logoTypes[] | null;

  loadingIndexationLogo: boolean;
  loadingArticleTypes: boolean;
  loadingAllArticleTypes: boolean;
  loadingArticleStatus: boolean;
  loadingReviewStatus: boolean;
  loadingAuthorContributions: boolean;
  loadingPublicationReviewType: boolean;
  loadingContributionList: boolean;
  loadingAffiliations: boolean;
  error: any | null;
}

const initialState: dimensionState = {
  articleTypes: null,
  allArticleTypes: null,
  articleStatus: null,
  reviewStatus: null,
  authorContributions: null,
  publicationReviewType: null,
  contributionList: null,
  affiliations: null,
  indexation_logo: null,
  indexationLogosByPublication: null,
  loadingIndexationLogo: false,
  loadingArticleTypes: false,
  loadingAllArticleTypes: true,
  loadingArticleStatus: false,
  loadingReviewStatus: false,
  loadingAuthorContributions: false,
  loadingPublicationReviewType: false,
  loadingContributionList: false,
  loadingAffiliations: false,
  error: null,
};

export const getArticleTypes = createAsyncThunk<
  dimensionsTypes[],
  void,
  { rejectValue: string }
>("dimensions/getArticleTypes", async (_, { rejectWithValue }) => {
  try {
    const response = await dimensionsService.getArticleTypes();
    return response;
  } catch (error: any) {
    return rejectWithValue(
      error.message || "Error al obtener los tipos de artículo",
    );
  }
});

export const getAllArticleTypes = createAsyncThunk<
  dimensionsTypes[],
  void,
  { rejectValue: string }
>("dimensions/getAllArticleTypes", async (_, { rejectWithValue }) => {
  try {
    const response = await dimensionsService.getAllArticleTypes();
    return response;
  } catch (error: any) {
    return rejectWithValue(error);
  }
});

export const deleteArticleTypes = createAsyncThunk<
  any,
  string,
  { rejectValue: string }
>(
  "dimensions/deleteArticleTypes",
  async (name: string, { rejectWithValue }) => {
    try {
      await dimensionsService.deleteArticleTypes(name);
      const updatedList = await dimensionsService.getArticleTypes();
      return { active: updatedList };
    } catch (error: any) {
      return rejectWithValue(
        error.message || "Error al eliminar el tipo de artículo",
      );
    }
  },
);

export const renameArticleTypes = createAsyncThunk<
  any,
  renameArticleTypesTypes,
  { rejectValue: string }
>(
  "dimensions/renameArticleTypes",
  async (
    renameArticleTypesRequest: renameArticleTypesTypes,
    { rejectWithValue },
  ) => {
    try {
      await dimensionsService.renameArticleTypes(renameArticleTypesRequest);
      const updatedList = await dimensionsService.getArticleTypes();
      const allList = await dimensionsService.getAllArticleTypes();
      return { active: updatedList, all: allList };
    } catch (error: any) {
      return rejectWithValue(
        error.message || "Error al renombrar el tipo de artículo",
      );
    }
  },
);

export const addArticleTypes = createAsyncThunk<
  any,
  string,
  { rejectValue: string }
>("dimensions/addArticleTypes", async (name: string, { rejectWithValue }) => {
  try {
    await dimensionsService.addArticleTypes(name);
    const updatedList = await dimensionsService.getArticleTypes();
    return { active: updatedList };
  } catch (error: any) {
    return rejectWithValue(
      error.message || "Error al agregar el tipo de artículo",
    );
  }
});

export const getArticleStatus = createAsyncThunk<
  dimensionsTypes[],
  void,
  { rejectValue: string }
>("dimensions/getArticleStatus", async (_, { rejectWithValue }) => {
  try {
    const response = await dimensionsService.getArticleStatus();
    return response;
  } catch (error: any) {
    return rejectWithValue(
      error.message || "Error al obtener el estado de los artículos",
    );
  }
});

export const getReviewStatus = createAsyncThunk<
  dimensionsTypes[],
  void,
  { rejectValue: string }
>("dimensions/getReviewStatus", async (_, { rejectWithValue }) => {
  try {
    const response = await dimensionsService.getReviewStatus();
    return response;
  } catch (error: any) {
    return rejectWithValue(
      error.message || "Error al obtener el estado de revisión",
    );
  }
});

export const getArticleAuthorContributions = createAsyncThunk<
  dimensionsTypes[],
  void,
  { rejectValue: string }
>(
  "dimensions/getArticleAuthorContributions",
  async (_, { rejectWithValue }) => {
    try {
      const response = await dimensionsService.getArticleAuthorContributions();
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.message || "Error al obtener las contribuciones de autores",
      );
    }
  },
);

export const getPublicationReviewType = createAsyncThunk<
  dimensionsTypes[],
  void,
  { rejectValue: string }
>("dimensions/getPublicationReviewType", async (_, { rejectWithValue }) => {
  try {
    const response = await dimensionsService.getPublicationReviewType();
    return response;
  } catch (error: any) {
    return rejectWithValue(
      error.message || "Error al obtener el tipo de revisión de publicación",
    );
  }
});

export const getContributionList = createAsyncThunk<
  dimensionsTypes[],
  void,
  { rejectValue: string }
>("dimensions/getContributionList", async (_, { rejectWithValue }) => {
  try {
    const response = await dimensionsService.getContributionList();
    return response;
  } catch (error: any) {
    return rejectWithValue(
      error.message || "Error al obtener la lista de contribuciones",
    );
  }
});

export const getAffiliations = createAsyncThunk<
  affiliationTypes[],
  void,
  { rejectValue: string }
>("dimensions/getAffiliations", async (_, { rejectWithValue }) => {
  const attempts = sessionStorage.getItem("affiliationFetchAttempts") || "0";
  const attemptCount = parseInt(attempts, 10);
  if (attemptCount >= 1) {
    return rejectWithValue(
      "Límite de intentos alcanzado para obtener afiliaciones",
    );
  }
  try {
    const response = await dimensionsService.getAffiliations();
    sessionStorage.setItem("affiliationFetchAttempts", "0"); // Reiniciar intentos en caso de éxito
    return response;
  } catch (error: any) {
    sessionStorage.setItem(
      "affiliationFetchAttempts",
      (attemptCount + 1).toString(),
    );
    return rejectWithValue(
      error.message || "Error al obtener las afiliaciones",
    );
  }
});

export const getIndexationLogo = createAsyncThunk<
  any,
  void,
  { rejectValue: string }
>("admin/getIndexationLogo", async (_, { rejectWithValue }) => {
  try {
    const res = await dimensionsService.getIndexationLogo();
    return res;
  } catch (error: any) {
    return rejectWithValue(error);
  }
});

export const addIndexationLogoToPublication = createAsyncThunk(
  "dimensions/addIndexationLogoToPublication",
  async (
    addIndexationLogoToPublicationRequest: addIndexationLogoToPublicationTypes,
    { rejectWithValue },
  ) => {
    try {
      return await dimensionsService.addIndexationLogoToPublication(
        addIndexationLogoToPublicationRequest,
      );
    } catch (error: any) {
      return rejectWithValue(
        error.message || "Error al agregar indexation logo",
      );
    }
  },
);

export const addIndexationLogosToPublication = createAsyncThunk(
  "dimensions/addIndexationLogosToPublication",
  async (
    addIndexationLogosToPublicationRequest: addIndexationLogosToPublicationTypes,
    { rejectWithValue },
  ) => {
    try {
      return await dimensionsService.addIndexationLogosToPublication(
        addIndexationLogosToPublicationRequest,
      );
    } catch (error: any) {
      return rejectWithValue(
        error.message || "Error al agregar múltiples indexation logos",
      );
    }
  },
);

export const removeIndexationLogoFromPublication = createAsyncThunk(
  "dimensions/removeIndexationLogoFromPublication",
  async (
    removeIndexationLogoFromPublicationRequest: addIndexationLogoToPublicationTypes,
    { rejectWithValue },
  ) => {
    try {
      return await dimensionsService.removeIndexationLogoFromPublication(
        removeIndexationLogoFromPublicationRequest,
      );
    } catch (error: any) {
      return rejectWithValue(
        error.message || "Error al eliminar indexation logo",
      );
    }
  },
);

export const removeIndexationLogosFromPublication = createAsyncThunk(
  "dimensions/removeIndexationLogosFromPublication",
  async (
    removeIndexationLogosFromPublicationRequest: addIndexationLogosToPublicationTypes,
    { rejectWithValue },
  ) => {
    try {
      return await dimensionsService.removeIndexationLogosFromPublication(
        removeIndexationLogosFromPublicationRequest,
      );
    } catch (error: any) {
      return rejectWithValue(
        error.message || "Error al eliminar múltiples indexation logos",
      );
    }
  },
);

export const getIndexationLogosByPublication = createAsyncThunk<
  indexation_logoTypes[],
  void,
  { rejectValue: string }
>(
  "dimensions/getIndexationLogosByPublication",
  async (_, { rejectWithValue }) => {
    try {
      return await dimensionsService.getIndexationLogosByPublication();
    } catch (error: any) {
      return rejectWithValue(
        error.message || "Error al obtener logos por publicación",
      );
    }
  },
);

const dimensionsSlice = createSlice({
  name: "dimensions",
  initialState,
  reducers: {
    resetError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getArticleTypes.fulfilled, (state, action) => {
      state.articleTypes = action.payload;
    });
    builder.addCase(getAllArticleTypes.fulfilled, (state, action) => {
      state.allArticleTypes = action.payload;
    });
    builder.addCase(getArticleStatus.fulfilled, (state, action) => {
      state.articleStatus = action.payload;
    });
    builder.addCase(getReviewStatus.fulfilled, (state, action) => {
      state.reviewStatus = action.payload;
    });
    builder.addCase(
      getArticleAuthorContributions.fulfilled,
      (state, action) => {
        state.authorContributions = action.payload;
      },
    );
    builder.addCase(getContributionList.fulfilled, (state, action) => {
      state.contributionList = action.payload;
    });
    builder.addCase(getAffiliations.fulfilled, (state, action) => {
      state.affiliations = action.payload;
    });
    builder.addCase(getPublicationReviewType.fulfilled, (state, action) => {
      state.publicationReviewType = action.payload;
    });
    builder.addCase(getIndexationLogo.fulfilled, (state, action) => {
      state.indexation_logo = action.payload;
    });

    builder.addMatcher(
      isAnyOf(
        deleteArticleTypes.fulfilled,
        renameArticleTypes.fulfilled,
        addArticleTypes.fulfilled,
      ),
      (state, action) => {
        state.articleTypes = action.payload.active;
        state.allArticleTypes = action.payload.all;
      },
    );

    builder.addMatcher(
      isAnyOf(
        getIndexationLogosByPublication.fulfilled,
        addIndexationLogoToPublication.fulfilled,
        addIndexationLogosToPublication.fulfilled,
        removeIndexationLogoFromPublication.fulfilled,
        removeIndexationLogosFromPublication.fulfilled,
      ),
      (state, action: PayloadAction<any>) => {
        state.indexationLogosByPublication = action.payload;
      },
    );
    const activeTypesActions = [
      getArticleTypes,
      deleteArticleTypes,
      renameArticleTypes,
      addArticleTypes,
    ];
    builder.addMatcher(
      isAnyOf(...activeTypesActions.map((a) => a.pending)),
      (state) => {
        state.loadingArticleTypes = true;
        state.error = null;
      },
    );
    builder.addMatcher(
      isAnyOf(
        ...activeTypesActions.map((a) => a.fulfilled),
        ...activeTypesActions.map((a) => a.rejected),
      ),
      (state) => {
        state.loadingArticleTypes = false;
      },
    );

    builder.addMatcher(isAnyOf(getAllArticleTypes.pending), (state) => {
      state.loadingAllArticleTypes = true;
      state.error = null;
    });
    builder.addMatcher(
      isAnyOf(getAllArticleTypes.fulfilled, getAllArticleTypes.rejected),
      (state) => {
        state.loadingAllArticleTypes = false;
      },
    );

    // Article Status
    builder.addMatcher(isAnyOf(getArticleStatus.pending), (state) => {
      state.loadingArticleStatus = true;
      state.error = null;
    });
    builder.addMatcher(
      isAnyOf(getArticleStatus.fulfilled, getArticleStatus.rejected),
      (state) => {
        state.loadingArticleStatus = false;
      },
    );

    // Review Status
    builder.addMatcher(isAnyOf(getReviewStatus.pending), (state) => {
      state.loadingReviewStatus = true;
      state.error = null;
    });
    builder.addMatcher(
      isAnyOf(getReviewStatus.fulfilled, getReviewStatus.rejected),
      (state) => {
        state.loadingReviewStatus = false;
      },
    );

    // Author Contributions
    builder.addMatcher(
      isAnyOf(getArticleAuthorContributions.pending),
      (state) => {
        state.loadingAuthorContributions = true;
        state.error = null;
      },
    );
    builder.addMatcher(
      isAnyOf(
        getArticleAuthorContributions.fulfilled,
        getArticleAuthorContributions.rejected,
      ),
      (state) => {
        state.loadingAuthorContributions = false;
      },
    );

    // Publication Review Type
    builder.addMatcher(isAnyOf(getPublicationReviewType.pending), (state) => {
      state.loadingPublicationReviewType = true;
      state.error = null;
    });
    builder.addMatcher(
      isAnyOf(
        getPublicationReviewType.fulfilled,
        getPublicationReviewType.rejected,
      ),
      (state) => {
        state.loadingPublicationReviewType = false;
      },
    );

    // Contribution List
    builder.addMatcher(isAnyOf(getContributionList.pending), (state) => {
      state.loadingContributionList = true;
      state.error = null;
    });
    builder.addMatcher(
      isAnyOf(getContributionList.fulfilled, getContributionList.rejected),
      (state) => {
        state.loadingContributionList = false;
      },
    );

    // Affiliations
    builder.addMatcher(isAnyOf(getAffiliations.pending), (state) => {
      state.loadingAffiliations = true;
      state.error = null;
    });
    builder.addMatcher(
      isAnyOf(getAffiliations.fulfilled, getAffiliations.rejected),
      (state) => {
        state.loadingAffiliations = false;
      },
    );

    // Indexation Logos (Grupo grande de acciones)
    const indexationActions = [
      getIndexationLogo,
      getIndexationLogosByPublication,
      addIndexationLogoToPublication,
      addIndexationLogosToPublication,
      removeIndexationLogoFromPublication,
      removeIndexationLogosFromPublication,
    ];
    builder.addMatcher(
      isAnyOf(...indexationActions.map((a) => a.pending)),
      (state) => {
        state.loadingIndexationLogo = true;
        state.error = null;
      },
    );
    builder.addMatcher(
      isAnyOf(
        ...indexationActions.map((a) => a.fulfilled),
        ...indexationActions.map((a) => a.rejected),
      ),
      (state) => {
        state.loadingIndexationLogo = false;
      },
    );

    builder.addMatcher(
      (action) => action.type.endsWith("/rejected"),
      (state, action: PayloadAction<string | undefined>) => {
        state.error = action.payload || "Ha ocurrido un error inesperado";
      },
    );
  },
});

export const { resetError } = dimensionsSlice.actions;
export default dimensionsSlice.reducer;
